import React, { createContext, useContext, useState, useEffect } from 'react';

const CurrencyContext = createContext();

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

// Supported currencies with their symbols and exchange rates (base: GBP)
const SUPPORTED_CURRENCIES = {
  GBP: { symbol: '£', name: 'British Pound', rate: 1.0 },
  USD: { symbol: '$', name: 'US Dollar', rate: 1.27 },
  EUR: { symbol: '€', name: 'Euro', rate: 1.17 },
  CAD: { symbol: 'C$', name: 'Canadian Dollar', rate: 1.72 },
  AUD: { symbol: 'A$', name: 'Australian Dollar', rate: 1.94 },
  JPY: { symbol: '¥', name: 'Japanese Yen', rate: 185.0 },
  CHF: { symbol: 'CHF', name: 'Swiss Franc', rate: 1.10 },
  SEK: { symbol: 'kr', name: 'Swedish Krona', rate: 13.2 },
  NOK: { symbol: 'kr', name: 'Norwegian Krone', rate: 13.5 },
  DKK: { symbol: 'kr', name: 'Danish Krone', rate: 8.7 },
  CNY: { symbol: '¥', name: 'Chinese Yuan', rate: 8.5 },
  INR: { symbol: '₹', name: 'Indian Rupee', rate: 105.0 },
  BRL: { symbol: 'R$', name: 'Brazilian Real', rate: 6.2 },
  MXN: { symbol: '$', name: 'Mexican Peso', rate: 21.0 },
  SGD: { symbol: 'S$', name: 'Singapore Dollar', rate: 1.7 },
  HKD: { symbol: 'HK$', name: 'Hong Kong Dollar', rate: 9.9 },
  NZD: { symbol: 'NZ$', name: 'New Zealand Dollar', rate: 2.1 },
  PLN: { symbol: 'zł', name: 'Polish Zloty', rate: 5.0 },
  CZK: { symbol: 'Kč', name: 'Czech Koruna', rate: 28.0 },
  HUF: { symbol: 'Ft', name: 'Hungarian Forint', rate: 450.0 },
  RUB: { symbol: '₽', name: 'Russian Ruble', rate: 95.0 },
  ZAR: { symbol: 'R', name: 'South African Rand', rate: 23.0 },
  KRW: { symbol: '₩', name: 'South Korean Won', rate: 1600.0 },
  THB: { symbol: '฿', name: 'Thai Baht', rate: 42.0 },
  MYR: { symbol: 'RM', name: 'Malaysian Ringgit', rate: 5.4 },
  PHP: { symbol: '₱', name: 'Philippine Peso', rate: 70.0 },
  IDR: { symbol: 'Rp', name: 'Indonesian Rupiah', rate: 19000.0 },
  VND: { symbol: '₫', name: 'Vietnamese Dong', rate: 30000.0 },
  TRY: { symbol: '₺', name: 'Turkish Lira', rate: 35.0 },
  ILS: { symbol: '₪', name: 'Israeli Shekel', rate: 4.6 },
  AED: { symbol: 'د.إ', name: 'UAE Dirham', rate: 4.7 },
  SAR: { symbol: '﷼', name: 'Saudi Riyal', rate: 4.8 },
  QAR: { symbol: '﷼', name: 'Qatari Riyal', rate: 4.6 },
  KWD: { symbol: 'د.ك', name: 'Kuwaiti Dinar', rate: 0.39 },
  BHD: { symbol: 'د.ب', name: 'Bahraini Dinar', rate: 0.48 },
  OMR: { symbol: '﷼', name: 'Omani Rial', rate: 0.49 },
  JOD: { symbol: 'د.ا', name: 'Jordanian Dinar', rate: 0.90 },
  LBP: { symbol: 'ل.ل', name: 'Lebanese Pound', rate: 1500.0 },
  EGP: { symbol: '£', name: 'Egyptian Pound', rate: 60.0 },
  MAD: { symbol: 'د.م.', name: 'Moroccan Dirham', rate: 12.0 },
  TND: { symbol: 'د.ت', name: 'Tunisian Dinar', rate: 3.1 },
  DZD: { symbol: 'د.ج', name: 'Algerian Dinar', rate: 170.0 },
  NGN: { symbol: '₦', name: 'Nigerian Naira', rate: 800.0 },
  GHS: { symbol: '₵', name: 'Ghanaian Cedi', rate: 15.0 },
  KES: { symbol: 'KSh', name: 'Kenyan Shilling', rate: 160.0 },
  UGX: { symbol: 'USh', name: 'Ugandan Shilling', rate: 4700.0 }
};

export const CurrencyProvider = ({ children }) => {
  const [selectedCurrency, setSelectedCurrency] = useState('USD');
  const [exchangeRates, setExchangeRates] = useState(SUPPORTED_CURRENCIES);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Load currency preference from localStorage
  useEffect(() => {
    const savedCurrency = localStorage.getItem('selectedCurrency');
    if (savedCurrency && SUPPORTED_CURRENCIES[savedCurrency]) {
      setSelectedCurrency(savedCurrency);
    }
  }, []);

  // Fetch real-time exchange rates from a reliable API
  useEffect(() => {
    fetchExchangeRates();
    
    // Update rates every 6 hours
    const interval = setInterval(fetchExchangeRates, 6 * 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchExchangeRates = async () => {
    try {
      // Using ExchangeRate-API (free tier) - you can replace with any reliable API
      const response = await fetch('https://api.exchangerate-api.com/v4/latest/GBP');
      const data = await response.json();
      
      if (data.rates) {
        const newRates = { ...SUPPORTED_CURRENCIES };
        
        // Update rates with real data
        Object.keys(newRates).forEach(currency => {
          if (data.rates[currency]) {
            newRates[currency].rate = data.rates[currency];
          }
        });
        
        setExchangeRates(newRates);
        setLastUpdated(new Date());
      }
    } catch (error) {
      console.warn('Failed to fetch exchange rates, using fallback rates:', error);
      // Keep using the fallback rates if API fails
    }
  };

  const changeCurrency = (currencyCode) => {
    if (SUPPORTED_CURRENCIES[currencyCode]) {
      setSelectedCurrency(currencyCode);
      localStorage.setItem('selectedCurrency', currencyCode);
    }
  };

  const convertCurrency = (amount, fromCurrency = 'GBP', toCurrency = selectedCurrency) => {
    if (!amount || amount === 0) return 0;
    
    // Convert from base currency (GBP) to target currency
    const fromRate = exchangeRates[fromCurrency]?.rate || 1;
    const toRate = exchangeRates[toCurrency]?.rate || 1;
    
    // Convert: amount / fromRate * toRate
    return (amount / fromRate) * toRate;
  };

  const formatCurrency = (amount, currency = selectedCurrency, options = {}) => {
    if (amount === null || amount === undefined) return '$0.00';
    
    const currencyInfo = SUPPORTED_CURRENCIES[currency];
    if (!currencyInfo) return '$0.00';
    
    const convertedAmount = convertCurrency(amount, 'GBP', currency);
    
    const defaultOptions = {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      ...options
    };
    
    try {
      return new Intl.NumberFormat('en-US', defaultOptions).format(convertedAmount);
    } catch (error) {
      // Fallback formatting
      return `${currencyInfo.symbol}${convertedAmount.toFixed(2)}`;
    }
  };

  const getCurrencySymbol = (currency = selectedCurrency) => {
    return SUPPORTED_CURRENCIES[currency]?.symbol || '$';
  };

  const getCurrencyName = (currency = selectedCurrency) => {
    return SUPPORTED_CURRENCIES[currency]?.name || 'US Dollar';
  };

  const getSupportedCurrencies = () => {
    return Object.entries(SUPPORTED_CURRENCIES).map(([code, info]) => ({
      code,
      symbol: info.symbol,
      name: info.name
    }));
  };

  const value = {
    selectedCurrency,
    changeCurrency,
    convertCurrency,
    formatCurrency,
    getCurrencySymbol,
    getCurrencyName,
    getSupportedCurrencies,
    exchangeRates,
    lastUpdated,
    isBaseCurrency: selectedCurrency === 'USD'
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};
